import React, { useState, useCallback } from 'react';
import { css } from '@emotion/core';
import {
  Modal,
  ModalControlModalProps,
  FormRow,
  Text,
  SecondaryButton,
  IconButton,
  XIcon,
} from '@weave/design-system';
import { theme } from '@weave/theme';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { searchOrgs, createMigration } from './api';
import { useAlert } from '@weave/alert-system';
import axios, { AxiosError } from 'axios';

interface OrgOption {
  orgId: string;
  name: string;
}

interface SourceEntry {
  id: number;
  searchResults: OrgOption[];
  isSearching: boolean;
  selectedOrg: OrgOption | null;
}

let nextSourceId = 1;
const createSourceEntry = (): SourceEntry => ({
  id: nextSourceId++,
  searchResults: [],
  isSearching: false,
  selectedOrg: null,
});

type Props = Pick<ModalControlModalProps, 'onClose'> & {
  onSuccess: () => void;
};

export const RequestMigrationModal = ({ onClose, onSuccess }: Props) => {
  const alert = useAlert();
  const [destOrgResults, setDestOrgResults] = useState<OrgOption[]>([]);
  const [isSearchingDest, setIsSearchingDest] = useState(false);
  const [selectedDestOrg, setSelectedDestOrg] = useState<OrgOption | null>(null);
  const [sourceEntries, setSourceEntries] = useState<SourceEntry[]>([
    createSourceEntry(),
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionProgress, setSubmissionProgress] = useState('');
  const [error, setError] = useState<string>('');

  const handleDestSearch = async (query: string) => {
    if (!query.trim()) {
      setDestOrgResults([]);
      return;
    }

    setIsSearchingDest(true);
    setError('');

    try {
      const response = await searchOrgs(query);
      const orgs = (response.orgs || [])
        .filter((result) => result.org && result.org.orgId && result.org.name)
        .map((result) => ({
          orgId: result.org!.orgId!,
          name: result.org!.name!,
        }));
      setDestOrgResults(orgs);
    } catch (err) {
      console.error('Error searching destination orgs:', err);
      setDestOrgResults([]);
    } finally {
      setIsSearchingDest(false);
    }
  };

  const handleSourceSearch = useCallback(async (entryId: number, query: string) => {
    if (!query.trim()) {
      setSourceEntries((prev) =>
        prev.map((e) => (e.id === entryId ? { ...e, searchResults: [] } : e))
      );
      return;
    }

    setSourceEntries((prev) =>
      prev.map((e) => (e.id === entryId ? { ...e, isSearching: true } : e))
    );
    setError('');

    try {
      const response = await searchOrgs(query);
      const orgs = (response.orgs || [])
        .filter((result) => result.org && result.org.orgId && result.org.name)
        .map((result) => ({
          orgId: result.org!.orgId!,
          name: result.org!.name!,
        }));
      setSourceEntries((prev) =>
        prev.map((e) => (e.id === entryId ? { ...e, searchResults: orgs } : e))
      );
    } catch (err) {
      console.error('Error searching source orgs:', err);
      setSourceEntries((prev) =>
        prev.map((e) => (e.id === entryId ? { ...e, searchResults: [] } : e))
      );
    } finally {
      setSourceEntries((prev) =>
        prev.map((e) => (e.id === entryId ? { ...e, isSearching: false } : e))
      );
    }
  }, []);

  const handleAddSource = () => {
    setSourceEntries((prev) => [...prev, createSourceEntry()]);
  };

  const handleRemoveSource = (entryId: number) => {
    setSourceEntries((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((e) => e.id !== entryId);
    });
  };

  const handleSourceSelect = (entryId: number, selected: OrgOption[]) => {
    const org = selected[0] || null;
    setSourceEntries((prev) =>
      prev.map((e) => (e.id === entryId ? { ...e, selectedOrg: org } : e))
    );
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const selectedSources = sourceEntries
      .map((e) => e.selectedOrg)
      .filter((org): org is OrgOption => org !== null);

    if (selectedSources.length === 0 || !selectedDestOrg) {
      setError('Please select a destination and at least one source organization');
      return;
    }

    if (sourceEntries.some((e) => e.selectedOrg === null)) {
      setError(
        'Please select an organization for each source entry, or remove empty entries'
      );
      return;
    }

    const duplicateIds = selectedSources.map((o) => o.orgId);
    if (new Set(duplicateIds).size !== duplicateIds.length) {
      setError('Duplicate source organizations are not allowed');
      return;
    }

    const sourceMatchingDest = selectedSources.find(
      (o) => o.orgId === selectedDestOrg.orgId
    );
    if (sourceMatchingDest) {
      setError('Source and destination organizations must be different');
      return;
    }

    setIsSubmitting(true);
    setError('');

    const total = selectedSources.length;

    for (let i = 0; i < total; i++) {
      const source = selectedSources[i];
      setSubmissionProgress(`Submitting ${i + 1} of ${total}...`);

      try {
        await createMigration(source.orgId, selectedDestOrg.orgId);
      } catch (err) {
        console.error(`Error creating migration for source ${source.orgId}:`, err);

        let errorMessage = 'An unexpected error occurred';
        if (axios.isAxiosError(err)) {
          const axiosError = err as AxiosError<any>;
          errorMessage =
            axiosError.response?.data?.message ||
            axiosError.response?.data?.error ||
            axiosError.message ||
            'Failed to create migration';
        }

        const context =
          i > 0
            ? `Failed on source ${i + 1} of ${total} (${
                source.name
              }). ${i} migration(s) were created successfully before this error.`
            : `Failed on source ${source.name}.`;

        setError(`${context} Error: ${errorMessage}`);
        setIsSubmitting(false);
        setSubmissionProgress('');
        return;
      }
    }

    setIsSubmitting(false);
    setSubmissionProgress('');
    alert.success(
      total === 1
        ? 'Migration created successfully'
        : `${total} migrations created successfully`
    );
    onSuccess();
  };

  const allSourcesSelected = sourceEntries.every((e) => e.selectedOrg !== null);
  const isFormValid = selectedDestOrg && allSourcesSelected && !isSubmitting;

  const primaryLabel = isSubmitting
    ? submissionProgress
    : sourceEntries.length > 1
    ? `Submit ${sourceEntries.length} Migrations`
    : 'Submit';

  return (
    <form onSubmit={handleSubmit}>
      <Modal.Header>Request New Migration</Modal.Header>
      <Modal.Body>
        <div css={modalBodyStyles}>
          <FormRow>
            <label css={labelStyles}>
              <Text weight="bold">Destination Organization</Text>
              <AsyncTypeahead
                id="dest-org-search"
                isLoading={isSearchingDest}
                delay={400}
                placeholder="Search by org name or ID..."
                labelKey={(option: OrgOption) => `${option.name} (${option.orgId})`}
                onChange={(selected: OrgOption[]) => {
                  const org = selected[0] || null;
                  setSelectedDestOrg(org);
                  setError('');
                }}
                onSearch={handleDestSearch}
                options={destOrgResults}
                emptyLabel="No organizations found"
                minLength={2}
                css={typeaheadStyles}
              />
            </label>
          </FormRow>

          <div css={sourceSectionStyles}>
            <Text weight="bold">Source Organization(s)</Text>
            {sourceEntries.map((entry, index) => (
              <div key={entry.id} css={sourceRowStyles}>
                <FormRow css={sourceFormRowStyles}>
                  <label css={labelStyles}>
                    {sourceEntries.length > 1 && (
                      <Text size="small" color="light">
                        Source {index + 1}
                      </Text>
                    )}
                    <AsyncTypeahead
                      id={`source-org-search-${entry.id}`}
                      isLoading={entry.isSearching}
                      delay={400}
                      placeholder="Search by org name or ID..."
                      labelKey={(option: OrgOption) => `${option.name} (${option.orgId})`}
                      onChange={(selected: OrgOption[]) =>
                        handleSourceSelect(entry.id, selected)
                      }
                      onSearch={(query: string) => handleSourceSearch(entry.id, query)}
                      options={entry.searchResults}
                      emptyLabel="No organizations found"
                      minLength={2}
                      css={typeaheadStyles}
                    />
                  </label>
                </FormRow>
                {sourceEntries.length > 1 && (
                  <IconButton
                    label="Remove source"
                    onClick={() => handleRemoveSource(entry.id)}
                    css={removeButtonStyles}
                  >
                    <XIcon />
                  </IconButton>
                )}
              </div>
            ))}
            <SecondaryButton
              size="small"
              onClick={handleAddSource}
              disabled={isSubmitting}
              css={addButtonStyles}
            >
              + Add Source Organization
            </SecondaryButton>
          </div>

          {error && (
            <div css={errorStyles}>
              <Text>{error}</Text>
            </div>
          )}

          <div css={infoTextStyles}>
            <Text>
              All source organizations will be merged into the destination organization.
              After migration is complete, the source organizations will no longer exist.
            </Text>
          </div>
        </div>
      </Modal.Body>
      <Modal.Actions
        primaryLabel={primaryLabel}
        onPrimaryClick="submit"
        onSecondaryClick={onClose}
        disablePrimary={!isFormValid}
      />
    </form>
  );
};

const modalBodyStyles = css`
  padding: ${theme.spacing(2, 0)};
`;

const labelStyles = css`
  display: block;
  width: 100%;

  > div:first-of-type {
    margin-bottom: ${theme.spacing(1)};
  }
`;

const typeaheadStyles = css`
  width: 100%;

  .rbt-input-main {
    width: 100%;
  }
`;

const sourceSectionStyles = css`
  margin-top: ${theme.spacing(2)};
`;

const sourceRowStyles = css`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing(1)};
  margin-top: ${theme.spacing(1)};
`;

const sourceFormRowStyles = css`
  flex: 1;
  margin-bottom: 0;
`;

const removeButtonStyles = css`
  margin-top: ${theme.spacing(1)};
  flex-shrink: 0;
`;

const addButtonStyles = css`
  margin-top: ${theme.spacing(1)};
`;

const errorStyles = css`
  margin-top: ${theme.spacing(2)};
  padding: ${theme.spacing(2)};
  background-color: ${theme.colors.critical10};
  border: 1px solid ${theme.colors.critical50};
  border-radius: 4px;
  color: ${theme.colors.critical50};
`;

const infoTextStyles = css`
  margin-top: ${theme.spacing(3)};
  padding: ${theme.spacing(2)};
  background-color: ${theme.colors.primary10};
  border-left: 3px solid ${theme.colors.primary50};
  color: ${theme.colors.neutral80};
`;
